import React, { ChangeEvent } from 'react';
import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { BRAZIL_LABEL_SECTIONS } from './brazilSectionCatalog';
import PAGE_MAPPING_QC_OPTIONS from './pageMappingQcOptions';
import { SectionPageValues } from './types';
import styles from './sectionMapping.styles';

type SectionPageFormProps = {
  pages: SectionPageValues;
  qcStatus: string;
  saving?: boolean;
  disabled?: boolean;
  /* eslint-disable no-unused-vars -- type-only callback signatures */
  onPageChange: (sectionKey: string, value: string) => void;
  onQcStatusChange: (value: string) => void;
  /* eslint-enable no-unused-vars */
  onSave: () => void;
  onClose: () => void;
};

const QC_OPTIONS = PAGE_MAPPING_QC_OPTIONS.map(option => option.value);

const SectionPageForm = ({
  pages,
  qcStatus,
  saving = false,
  disabled = false,
  onPageChange,
  onQcStatusChange,
  onSave,
  onClose
}: SectionPageFormProps) => {
  const busy = disabled || saving;

  return (
    <Box component='form' onSubmit={event => event.preventDefault()} sx={styles.form}>
      <Stack spacing={2}>
        {BRAZIL_LABEL_SECTIONS.map(section => (
          <TextField
            key={section.key}
            type='number'
            label={section.label}
            value={pages[section.key] ?? '0'}
            disabled={busy}
            inputProps={{ min: 0 }}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onPageChange(section.key, event.target.value)
            }
          />
        ))}

        {/* eslint-disable react/jsx-props-no-spreading */}
        <Autocomplete
          id='page-mapping-qc-status'
          fullWidth
          options={QC_OPTIONS}
          value={qcStatus}
          disabled={busy}
          getOptionLabel={value =>
            PAGE_MAPPING_QC_OPTIONS.find(option => option.value === value)?.label ?? value
          }
          onChange={(_, newValue) => {
            if (newValue) {
              onQcStatusChange(newValue);
            }
          }}
          renderInput={params => (
            <TextField {...params} label='QC Status' variant='outlined' required />
          )}
        />
        {/* eslint-enable react/jsx-props-no-spreading */}
      </Stack>

      <Stack direction='row' spacing={2} sx={styles.formActions}>
        <Button
          type='button'
          variant='contained'
          color='primary'
          disabled={busy}
          sx={styles.actionButton}
          onClick={onSave}>
          <Typography>{saving ? 'Saving…' : 'Save'}</Typography>
        </Button>
        <Button
          type='button'
          variant='outlined'
          disabled={saving}
          sx={styles.actionButton}
          onClick={onClose}>
          <Typography>Close</Typography>
        </Button>
      </Stack>
    </Box>
  );
};

SectionPageForm.defaultProps = {
  saving: false,
  disabled: false
};

export default SectionPageForm;
